import React, { Fragment } from "react";
import { IRoutePricePreview } from "./RoutePriceComponent";
import {
  Box,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { calculateTotal } from "../tools";
import Collapse from "@material-ui/core/Collapse";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Tooltip from "@material-ui/core/Tooltip";
import CustomPriceField from "../../../components/CustomPriceField";

interface IPricePreviewTable {
  vehicleSize: string;
  index: number;
  lastData?: IRoutePricePreview;
  currentData?: IRoutePricePreview;
  expanded: boolean;
  onToggle: () => void;
}

type TableColumnKey =
  | "baseAmount"
  | "adjustmentAmount"
  | "busAssistantIncentive"
  | "schoolIncentive"
  | "additionalIncentive";

const useStyles = makeStyles({
  tableMargin: {
    marginBottom: "20px",
    marginTop: "-8px",
  },
  changedCell: {
    backgroundColor: "#d4edda", // light green
    color: "#155724", // dark green text
  },
  toggleButton: {
    marginLeft: "8px",
  },
  buttonContainer: {
    marginBottom: "4px",
  },
});

const RoutePricePreviewTable = ({
  vehicleSize,
  index,
  lastData,
  currentData,
  expanded,
  onToggle,
}: IPricePreviewTable) => {
  const classes = useStyles();

  // Price columns to display
  const fields: { label: string; key: TableColumnKey }[] = [
    { label: "Base Amount", key: "baseAmount" },
    { label: "Adjustment", key: "adjustmentAmount" },
    { label: "Assistant Incentive", key: "busAssistantIncentive" },
    { label: "School Incentive", key: "schoolIncentive" },
    { label: "Additional Incentive", key: "additionalIncentive" },
  ];

  const componentKey = `${vehicleSize}-${index}`;

  return (
    <Fragment>
      <Box
        display="flex"
        alignItems="center"
        className={classes.buttonContainer}
      >
        <Typography variant="subtitle1">{vehicleSize} Vehicle</Typography>
        <Tooltip title={expanded ? "Hide table" : "Show table"}>
          <IconButton
            size="small"
            onClick={onToggle}
            className={classes.toggleButton}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Table size="small" className={classes.tableMargin}>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              {fields.map(({ label, key }) => (
                <TableCell key={`${componentKey}-header-${key}`} align="right">
                  {label}
                </TableCell>
              ))}
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Render new adjustment price preview first, then last month */}
            {[currentData, lastData].map((entry, sideIndex) => (
              <TableRow key={`${componentKey}-row-${sideIndex}`}>
                <TableCell>
                  <strong>
                    {sideIndex === 0 ? "New Price" : "Last Month Price"}
                  </strong>
                </TableCell>

                {/* Display individual columns with optional highlight if adjustment price is different */}
                {fields.map(({ key }) => {
                  const isHighlight =
                    sideIndex === 0 &&
                    key === "adjustmentAmount" &&
                    lastData.adjustmentAmount !== currentData.adjustmentAmount;
                  return (
                    <TableCell
                      key={`${componentKey}-cell-${key}-${sideIndex}`}
                      align="right"
                      className={isHighlight ? classes.changedCell : undefined}
                    >
                      <CustomPriceField record={entry} source={key} />
                    </TableCell>
                  );
                })}

                <TableCell
                  align="right"
                  className={
                    sideIndex === 0 &&
                    lastData.adjustmentAmount !== currentData.adjustmentAmount
                      ? classes.changedCell
                      : undefined
                  }
                >
                  {`$ ${(calculateTotal(entry) / 100).toFixed(2)}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Fragment>
  );
};

export default RoutePricePreviewTable;
